
import axios from 'axios';

// URL base para las peticiones API
const API_URL = 'https://backend-production-1786.up.railway.app/api';

// Interceptor para incluir el token en las peticiones
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Servicio para gestionar operaciones relacionadas con planes y suscripciones
 */
class PlanService {
  /**
   * Obtiene todos los planes disponibles para el usuario actual
   * @returns {Promise} Planes disponibles
   */
  async getPlanes() {
    try {
      const response = await axios.get(`${API_URL}/suscripciones/planes`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener planes:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtiene información de la suscripción actual del usuario
   * @returns {Promise} Información de suscripción
   */
  async getSuscripcionInfo() {
    try {
      const response = await axios.get(`${API_URL}/suscripciones/info`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener información de suscripción:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Simula un cambio de plan y calcula el prorrateado
   * @param {Number} planId - ID del nuevo plan
   * @param {String} cicloFacturacion - Ciclo de facturación ('mensual' o 'anual')
   * @returns {Promise} Simulación del cambio
   */
  async simularCambioPlan(planId, cicloFacturacion) {
    try {
      // Verificar que el ID de plan sea válido
      if (!planId || isNaN(parseInt(planId))) {
        throw new Error('ID de plan inválido');
      }
      
      console.log(`Simulando cambio de plan - ID: ${planId}, Ciclo: ${cicloFacturacion}`);
      
      // Obtener información del usuario del localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Tipo de usuario:', userData.tipo_usuario_id);
      
      const response = await axios.post(`${API_URL}/suscripciones/simular-cambio`, {
        planId,
        cicloFacturacion,
        tipoUsuarioId: userData.tipo_usuario_id // Incluir explícitamente el tipo de usuario
      });
      
      return response.data;
    } catch (error) {
      console.error('Error detallado al simular cambio de plan:', error);
      if (error.response && error.response.data) {
        console.error('Respuesta del servidor:', error.response.data);
      }
      throw this.handleError(error);
    }
  }

  /**
   * Realiza el cambio de plan
   * @param {Number} planId - ID del nuevo plan
   * @param {String} cicloFacturacion - Ciclo de facturación ('mensual' o 'anual')
   * @param {String} codigoVerificacion - Código de verificación (opcional)
   * @returns {Promise} Resultado del cambio
   */
  async cambiarPlan(planId, cicloFacturacion, codigoVerificacion = null) {
    try {
      // Verificar que el ID de plan sea válido
      if (!planId || isNaN(parseInt(planId))) {
        throw new Error('ID de plan inválido');
      }
      
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      const payload = {
        planId,
        cicloFacturacion,
        tipoUsuarioId: userData.tipo_usuario_id // Incluir explícitamente el tipo de usuario
      };

      // Si hay código de verificación, incluirlo
      if (codigoVerificacion) {
        payload.codigoVerificacion = codigoVerificacion;
      }

      const response = await axios.post(`${API_URL}/suscripciones/cambiar-plan`, payload);
      return response.data;
    } catch (error) {
      console.error('Error al cambiar plan:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancela un cambio de plan pendiente
   * @param {Number} cambioId - ID del cambio a cancelar
   * @returns {Promise} Resultado de la cancelación
   */
  async cancelarCambioPendiente(cambioId) {
    try {
      const response = await axios.post(`${API_URL}/suscripciones/cancelar-cambio`, {
        cambioId
      });
      return response.data;
    } catch (error) {
      console.error('Error al cancelar cambio pendiente:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtiene el historial de cambios de plan
   * @returns {Promise} Historial de cambios
   */
  async getHistorialCambios() {
    try {
      const response = await axios.get(`${API_URL}/suscripciones/historial`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener historial de cambios:', error);
      throw this.handleError(error);
    }
  }

  /**
 * Actualiza el método de pago predeterminado del usuario
 * @param {String} metodoPago - Método de pago ('plan' o 'payu')
 * @returns {Promise} Resultado de la operación
 */
async actualizarMetodoPago(metodoPago) {
  try {
    const response = await axios.post(`${API_URL}/suscripciones/metodo-pago`, {
      metodoPago
    });
    return response.data;
  } catch (error) {
    console.error('Error al actualizar método de pago:', error);
    throw this.handleError(error);
  }
}

/**
 * Aplica un cambio de plan después de un pago exitoso con PayU
 * @param {Number} planId - ID del plan
 * @param {String} cicloFacturacion - Ciclo de facturación
 * @param {String} transactionId - ID de la transacción de PayU
 * @param {String} metodoPago - Método de pago ('payu')
 * @returns {Promise} Resultado del cambio
 */
async aplicarCambioPlanPagado(planId, cicloFacturacion, transactionId, metodoPago) {
  try {
    const response = await axios.post(`${API_URL}/suscripciones/aplicar-pago-payu`, {
      planId,
      cicloFacturacion,
      transactionId,
      metodoPago
    });
    return response.data;
  } catch (error) {
    console.error('Error al aplicar cambio después del pago:', error);
    throw this.handleError(error);
  }
}

/**
 * Obtiene el historial de pagos del usuario
 * @returns {Promise} Historial de pagos
 */
async getHistorialPagos() {
  try {
    const response = await axios.get(`${API_URL}/pagos/history`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener historial de pagos:', error);
    throw this.handleError(error);
  }
}

  /**
   * Maneja errores de las peticiones
   * @param {Error} error - Error de la petición
   * @returns {Error} Error procesado
   */
  handleError(error) {
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      const errorMsg = error.response.data.error || error.response.data.message || 'Error desconocido';
      console.error('Respuesta de error del servidor:', error.response.data);
      return new Error(errorMsg);
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      console.error('No se recibió respuesta del servidor:', error.request);
      return new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
    } else {
      // Algo ocurrió al configurar la petición
      console.error('Error al configurar la petición:', error.message);
      return error;
    }
  }
}

// Exporta una instancia del servicio
export default new PlanService();